import React, { useState, useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom'; // Add useLocation here
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import MuiAppBar from '@mui/material/AppBar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TaskIcon from '@mui/icons-material/Task';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import logo from '../../assets/Images/Clanbridge-logo-White.png';

const drawerWidth = 200;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open, visible }) => ({
    width: visible ? drawerWidth : 0, // Hide the drawer completely if not visible
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && visible && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && visible && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
    // When not visible, we hide the drawer completely
    ...(visible === false && {
      display: 'none', // Disable hover interaction and hide the drawer content
    }),
  })
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '64px',
  backgroundColor: '#635bff',
  color: '#fff',
  padding: theme.spacing(0, 1),
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

function TicketServiceSideBar() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation(); // Hook to track the current route
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userName, setUserName] = useState('');
  const [loginType, setLoginType] = useState('');
  const [visible, setVisible] = useState(true); // Track visibility of the drawer

  useEffect(() => {
    const storedUserName = localStorage.getItem('userName');
    if (storedUserName) {
      setUserName(storedUserName);
    }
  }, []);
 
  
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    // Show the side menu only on "/tkt-dashboard" page
    if (location.pathname === '/tkt-dashboard') {
      setVisible(true);  // Show the drawer content on the dashboard page
      setOpen(false);    // Close the drawer initially when revisiting the dashboard
    } else {
      setVisible(false); // Hide the drawer content on other pages
      setOpen(false);    // Ensure the drawer is closed on other pages
    }
  }, [location]);

  const handleMenuClick = (path) => {
    navigate(path);
  };

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('userName');
    localStorage.removeItem('isLoggedIn');
    navigate('/m-login')
    handleProfileClose();
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon sx={{ color: '#635bff' }} />, path: '/tkt-dashboard' },
    { text: 'Raised Ticket', icon: <TaskIcon sx={{ color: '#635bff' }} />, path: '/raise-ticket' },
    { text: 'Track Ticket', icon: <TrackChangesIcon sx={{ color: '#635bff' }} />, path: '/track-ticket' },
    { text: 'Task Assignment', icon: <AssignmentIcon sx={{ color: '#635bff' }} />, path: '#' },
    { text: 'Task Progress', icon: <ChangeCircleIcon sx={{ color: '#635bff' }} />, path: '#' },
  ];

  const isSidebarVisible = location.pathname === '/tkt-dashboard';

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar  position="fixed" open={isSidebarVisible && open}>
        <Toolbar sx={{ backgroundColor: '#635bff' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            <img src={logo} style={{ height: '45px', margin: '8px 0 0 20px' }} alt="logo" />
          </Typography>
          <IconButton
            onClick={handleProfileClick}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={Boolean(anchorEl) ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
          >
            <Avatar sx={{ width: 32, height: 32 }}>{userName[0]}</Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl} 
            id="account-menu"
            open={Boolean(anchorEl)}
            onClose={handleProfileClose}
          >
            <MenuItem>Hello, {userName} ({loginType})</MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={open}
        visible={visible} // Pass the visibility to the Drawer component
        onMouseEnter={() => visible && setOpen(true)} // Only enable hover if visible
        onMouseLeave={() => visible && setOpen(false)} // Only enable hover if visible
      >
        <DrawerHeader>
          <Typography variant="h6">Maintenance App</Typography>
        </DrawerHeader>
        <Divider />
        
        <List>
          {menuItems.map((item, index) => (
            <ListItem key={index} disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                onClick={() => handleMenuClick(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </Box>
  );
}

export default TicketServiceSideBar;
